/** Hub vs Branch location types (legacy middle_destination reads as Hub). */

export function normalizeDestinationType(type) {
  if (!type) return type;
  const value = String(type).trim().toLowerCase();
  if (value === 'middle_destination') return 'hub_destination';
  return value;
}

export function destinationTypeLabel(type) {
  const normalized = normalizeDestinationType(type);
  if (normalized === 'hub_destination') return 'Hub';
  if (normalized === 'branch_destination') return 'Branch';
  return type ? String(type).replace(/_/g, ' ') : '';
}

export function isHubDestination(type) {
  return normalizeDestinationType(type) === 'hub_destination';
}

export function isBranchDestination(type) {
  return normalizeDestinationType(type) === 'branch_destination';
}

/** Strip legacy "Hub - Branch" prefix; return branch/location name only. */
export function stripHubPrefixFromLabel(label) {
  if (label == null) return '';
  const name = String(label).trim();
  if (!name) return '';
  if (name.includes(' - ')) {
    const branch = name.split(' - ').slice(1).join(' - ').trim();
    return branch || name;
  }
  return name;
}

/** Display / stored label: destination name only (hub or branch). */
export function formatHubBranchLabel(hub, _hubsById = null) {
  if (!hub) return '';
  const branchName = (hub.location || hub.hub_name || hub.name || '').trim();
  if (branchName) return branchName;
  return stripHubPrefixFromLabel(hub.display_label);
}

/** User/agent dropdown label: "Name (Location)". */
export function formatUserWithLocation(user, hubsById = null) {
  if (!user) return '';
  const name = (user.full_name || user.name || user.username || user.agent_name || '').trim();
  if (!name) return '';

  let loc = '';
  const hubId = user.location_hub_id ?? user.hub_id ?? null;
  if (hubId != null && hubsById) {
    const hub = hubsById[hubId] || hubsById[String(hubId)];
    if (hub) loc = formatHubBranchLabel(hub, hubsById);
  }
  if (!loc) {
    loc = stripHubPrefixFromLabel(
      user.location_hub_label ||
        user.location_hub_name ||
        user.location_label ||
        user.location ||
        user.city ||
        '',
    );
  }

  return loc ? `${name} (${loc})` : name;
}

function userRoleName(user) {
  if (!user) return '';
  if (typeof user.role === 'string') return user.role.trim();
  return String(user.role_name || user.role?.role_name || '').trim();
}

function userHubLabel(user, hubsById = null) {
  if (!user) return '';
  const hubId = user.location_hub_id ?? user.hub_id ?? null;
  if (hubId != null && hubsById) {
    const hub = hubsById[hubId] || hubsById[String(hubId)];
    if (hub) return formatHubBranchLabel(hub, hubsById);
  }
  return stripHubPrefixFromLabel(
    user.location_hub_label ||
      user.location_hub_name ||
      user.location_label ||
      user.location ||
      user.city ||
      '',
  );
}

/** Daily Branch user filter: "Full name - Role - Location hub". */
export function formatUserNameRoleHub(user, hubsById = null) {
  if (!user) return '';
  const name = (user.full_name || user.name || user.username || user.agent_name || '').trim();
  if (!name) return '';
  const role = userRoleName(user);
  const hub = userHubLabel(user, hubsById);
  return [name, role, hub].filter(Boolean).join(' - ');
}

/** GC Number prefix from a single hub (legacy / fallback). */
export function getGcPrefixCodes(hub) {
  if (!hub) return { hubCode: null, branchCode: null, prefix: null };
  const code = (hub.location_code || '').trim().toUpperCase() || null;
  return {
    hubCode: code,
    branchCode: code,
    prefix: code,
  };
}

/** GC Number prefix from sender location hub code → "KPM". */
export function getGcRoutePrefix(senderHub, _receiverHub) {
  const hubCode = (senderHub?.location_code || '').trim().toUpperCase() || null;
  return {
    hubCode,
    branchCode: hubCode,
    prefix: hubCode,
  };
}

/** Indian FY tag, e.g. 01-04-2026 to 31-03-2027 → "26-27". */
export function getFinancialYearSuffix(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: 'numeric',
  }).formatToParts(date);
  const year = Number(parts.find((p) => p.type === 'year')?.value);
  const month = Number(parts.find((p) => p.type === 'month')?.value);
  const fyStart = month >= 4 ? year : year - 1;
  return `${String(fyStart).slice(-2)}-${String(fyStart + 1).slice(-2)}`;
}

/** GC payment suffix: PD (paid), TP (to pay), CRD (credit). */
export function getGcPaymentCode(modeOfPayment) {
  const key = String(modeOfPayment || '').trim().toLowerCase();
  if (key === 'to_pay') return 'TP';
  if (key === 'credit') return 'CRD';
  return 'PD';
}

/** Two-digit year for GC Number, e.g. 2026 → "26". */
export function getGcYearSuffix(date = new Date()) {
  return String(date.getFullYear()).slice(-2);
}

/** Payment + year tokens: PD and 26 → used as SENDER-RECEIVER-PD-26-SEQ. */
export function getGcPaymentYearSuffix(modeOfPayment, date = new Date()) {
  return `${getGcPaymentCode(modeOfPayment)}-${getGcYearSuffix(date)}`;
}

/** Keep only branch destinations. */
export function filterBranchLocations(hubs = []) {
  return (Array.isArray(hubs) ? hubs : []).filter((h) =>
    isBranchDestination(h?.destination_type),
  );
}

export function filterHubLocations(hubs = []) {
  return (Array.isArray(hubs) ? hubs : []).filter((h) =>
    isHubDestination(h?.destination_type),
  );
}

/** Hub + branch destinations for shipment From/To pickers. */
export function filterShipmentLocations(hubs = []) {
  return (Array.isArray(hubs) ? hubs : []).filter(
    (h) =>
      isBranchDestination(h?.destination_type) ||
      isHubDestination(h?.destination_type),
  );
}
